import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AWARD_PAYMENT } from '../../constants/general';
import '../../tailwind.css';
import PleaseWait from '../Shared/PleaseWait';
import AwardPaymentRequestContent from './AwardPaymentRequestContent';
import { getMostRecentUnderReviewSchoolInfo, mapAwardPaymentRequestDetailsToFormState, mapSeniorToAwardPaymentYourInformationState } from '.';
import { normalizePaymentRequestStatus, SAVE_ALLOWED_STATUSES } from './paymentRequestStatuses';
import TitleBar from '../Shared/TitleBar';
import { inject, observer } from 'mobx-react';
import { isAdminOrFHT, isScholarshipCoordinator, isStudentOrAlumni } from '../../util/miscUtil';
import AwardPaymentLayout from './AwardPaymentLayout';
import { AuthStoreShape } from '../../types/authStore';
import { callApi } from '../../util/apiWrapper';
import API from '../../constants/api';
import { METHOD } from '../../constants/method';
import { logException } from '../../services/appInsights';
import type { AwardPaymentRequestApiDetails, PaymentRequestByIdResponse } from './AwardPaymentRequestContent.types';
import AwardPaymentPageHeader from './AwardPaymentPageHeader';
import AwardPaymentPageContainer from './AwardPaymentPageContainer';
import type { YourInformationFormState } from './RequestYourInformation';
import { useLoadingWithMinTime } from '../../hooks/useLoadingWithMinTime';
import { buildAwardPaymentTitleBarPaths } from '../../util/awardPaymentTitleBar';
import PATH from '../../constants/paths';

type ErrorAlertProps = { children: React.ReactNode; dataTestId?: string };
const ErrorAlert: React.FC<ErrorAlertProps> = ({ children, dataTestId }) => (
  <div data-testid={dataTestId} className='w-full p-4 bg-red-50 border border-red-300 rounded-lg text-red-800'>
    {children}
  </div>
);

type Props = { AuthStore?: AuthStoreShape; SeniorStore?: any };

const AwardPaymentRequestComponent: React.FC<Props> = (props: Props) => {
  const authStore = props.AuthStore ?? { roles: [], isLoading: true, getToken: null };
  const store = props.SeniorStore ?? { senior: { userId: 0 } };
  const mappedSeniorFromStore = React.useMemo(() => mapSeniorToAwardPaymentYourInformationState(store?.senior), [store?.senior]);
  const seniorFromSession = React.useMemo(() => {
    try {
      if (typeof window === 'undefined') return null;
      const rawState = sessionStorage.getItem('SeniorStore');
      if (!rawState) return null;
      return JSON.parse(rawState)?.senior ?? null;
    } catch (error) {
      logException(error);
      return null;
    }
  }, []);
  const requestIdParam = new URLSearchParams(window.location.search).get('id');
  const requestUserIdParam = new URLSearchParams(window.location.search).get('userId');
  const requestId = requestIdParam ? Number(requestIdParam) : NaN;
  const requestUserIdFromQuery = requestUserIdParam ? Number(requestUserIdParam) : 0;
  const isViewingExistingRequest = Number.isFinite(requestId) && requestId > 0;
  const mappedSeniorFromSession = React.useMemo(() => mapSeniorToAwardPaymentYourInformationState(seniorFromSession), [seniorFromSession]);
  const initialPrefilledYourInformationState = React.useMemo(() => {
    if (isViewingExistingRequest) {
      return null;
    }

    return mappedSeniorFromStore ?? mappedSeniorFromSession;
  }, [isViewingExistingRequest, mappedSeniorFromStore, mappedSeniorFromSession]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingRequestDetails, setIsLoadingRequestDetails] = useState(false);
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(false);
  const [requestDetails, setRequestDetails] = useState<AwardPaymentRequestApiDetails | null>(null);
  const [requestDetailsError, setRequestDetailsError] = useState<string | null>(null);
  const [prefilledYourInformationState, setPrefilledYourInformationState] = useState<YourInformationFormState | null>(
    initialPrefilledYourInformationState
  );
  const recentSchoolInfoRequestRef = React.useRef<number | null>(null);

  useEffect(() => {
    if (!prefilledYourInformationState && initialPrefilledYourInformationState) {
      setPrefilledYourInformationState(initialPrefilledYourInformationState);
    }
  }, [initialPrefilledYourInformationState, prefilledYourInformationState]);

  const userIdForSeniorFetch = React.useMemo(() => {
    if (isAdminOrFHT(authStore.roles)) {
      return store?.senior?.userId ?? 0;
    }

    if (isStudentOrAlumni(authStore.roles)) {
      return authStore?.user?.result?.id ?? 0;
    }

    return 0;
  }, [authStore.roles, authStore.user?.result?.id, store?.senior?.userId]);

  useEffect(() => {
    setIsLoading(Boolean(props.AuthStore?.isLoading));
  }, [props.AuthStore?.isLoading]);

  useEffect(() => {
    if (isViewingExistingRequest || isLoading || !authStore.getToken || !userIdForSeniorFetch) {
      return;
    }

    if (recentSchoolInfoRequestRef.current === userIdForSeniorFetch) {
      return;
    }

    let isMounted = true;
    const loadSeniorForPrefill = async () => {
      recentSchoolInfoRequestRef.current = userIdForSeniorFetch;
      setIsLoadingPrefill(true);
      try {
        let mappedPrefill = prefilledYourInformationState ?? initialPrefilledYourInformationState ?? null;

        if (!mappedPrefill) {
          const fetchedSenior = await store.loadSeniorByUserId(authStore, userIdForSeniorFetch);
          mappedPrefill = mapSeniorToAwardPaymentYourInformationState(fetchedSenior);
        }

        if (!isMounted) {
          return;
        }

        let recentRequestSchoolInfo = { dateOfEnrollment: '', plannedGraduationDate: '' };
        try {
          const { fetchAwardPaymentRequests } = await import('../Pages/AwardPaymentRequestsSummary');
          const recentRequests = await fetchAwardPaymentRequests(authStore, userIdForSeniorFetch);
          recentRequestSchoolInfo = getMostRecentUnderReviewSchoolInfo(recentRequests);
        } catch (requestError) {
          logException(requestError);
        }

        if (mappedPrefill) {
          const nextState = {
            ...mappedPrefill,
            schoolInfo: {
              ...mappedPrefill.schoolInfo,
              ...recentRequestSchoolInfo,
            },
          };
          setPrefilledYourInformationState(nextState);
        }
      } catch (error) {
        logException(error);
      } finally {
        if (isMounted) {
          setIsLoadingPrefill(false);
        }
      }
    };

    void loadSeniorForPrefill();

    return () => {
      isMounted = false;
    };
  }, [
    authStore.getToken,
    initialPrefilledYourInformationState,
    isLoading,
    isViewingExistingRequest,
    prefilledYourInformationState,
    store,
    userIdForSeniorFetch,
  ]);

  useEffect(() => {
    if (!isViewingExistingRequest || isLoading || !authStore.getToken) {
      return;
    }

    let isMounted = true;
    const loadRequestDetails = async () => {
      setIsLoadingRequestDetails(true);
      setRequestDetailsError(null);

      try {
        const response = (await callApi(
          authStore,
          API.PAYMENT_REQUEST_BY_ID.replace('{id}', requestId.toString()),
          METHOD.GET
        )) as PaymentRequestByIdResponse;
        const fetchedRequest = response?.result ?? null;
        if (isMounted) {
          setRequestDetails(fetchedRequest);
        }
      } catch (error) {
        logException(error);
        if (isMounted) {
          setRequestDetailsError('Unable to load request details.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingRequestDetails(false);
        }
      }
    };

    void loadRequestDetails();

    return () => {
      isMounted = false;
    };
  }, [authStore, isLoading, isViewingExistingRequest, requestId]);

  const requestOwnerUserId = requestDetails?.user_id ?? (Number.isFinite(requestUserIdFromQuery) ? requestUserIdFromQuery : 0);
  const history = useHistory();
  const senior = props.SeniorStore?.senior ?? {};
  const seniorDisplayName = [senior?.alumni_updated_first_name ?? senior?.first_name, senior?.alumni_updated_last_name ?? senior?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const isUserAlumni = !!props.SeniorStore?.alumni;
  const requestContextUserId = requestOwnerUserId || store?.senior?.userId || requestUserIdFromQuery || 0;

  const titleBarPaths = React.useMemo(
    () =>
      buildAwardPaymentTitleBarPaths({
        history,
        roles: authStore.roles ?? [],
        SeniorStore: props.SeniorStore,
        isAdminOrFHTRole: isAdminOrFHT(authStore.roles),
        isUserAlumni,
        seniorDisplayName,
        requestContextUserId,
        includeAwardPaymentList: isAdminOrFHT(authStore.roles),
        awardPaymentListPath: PATH.AWARD_PAYMENT_REQUESTS,
        awardPaymentListName: AWARD_PAYMENT.TITLE,
      }),
    [authStore.roles, history, isUserAlumni, props.SeniorStore, requestContextUserId, seniorDisplayName]
  );

  useEffect(() => {
    if (!isViewingExistingRequest || isLoading || !authStore.getToken || !requestOwnerUserId || typeof store.loadSeniorByUserId !== 'function') {
      return;
    }

    let isMounted = true;
    const loadSeniorForSelectedRequest = async () => {
      try {
        const senior = await store.loadSeniorByUserId(authStore, requestOwnerUserId);
        if (!senior || !isMounted) {
          return;
        }
      } catch (error) {
        logException(error);
      }
    };

    void loadSeniorForSelectedRequest();

    return () => {
      isMounted = false;
    };
  }, [authStore, isLoading, isViewingExistingRequest, requestOwnerUserId, store]);

  const isExistingRequestEditable = Boolean(requestDetails && SAVE_ALLOWED_STATUSES.has(normalizePaymentRequestStatus(requestDetails?.status)));

  const requestNumber = requestDetails?.requestNumber || requestDetails?.id || '';
  const headerTitle = requestNumber ? `Award Payment Request  #  ${requestNumber}` : AWARD_PAYMENT.NEW_AWARD_PAYMENT_REQUEST.TITLE;
  const title = requestNumber ? `Request # ${requestNumber}` : AWARD_PAYMENT.NEW_AWARD_PAYMENT_REQUEST.BREADCRUMB_TITLE;
  const bannerTitle = isStudentOrAlumni(authStore.roles) ? 'Award Payment Request' : `Award Payment Request for ${seniorDisplayName}`;

  const shouldShowSpinner =
    isLoading ||
    !authStore.getToken ||
    (isViewingExistingRequest && isLoadingRequestDetails) ||
    (!isViewingExistingRequest && isLoadingPrefill) ||
    isSubmitting;
  const showSpinnerWithMinTime = useLoadingWithMinTime(shouldShowSpinner, 2000);

  const isAdminOrFHTRole = isAdminOrFHT(authStore.roles);
  const isStudentOrAlumniRole = isStudentOrAlumni(authStore.roles);
  const isScholarshipCoordinatorUser = isScholarshipCoordinator(authStore.roles, authStore?.user?.result?.position_title);

  const isAwardFormEditable = isExistingRequestEditable && (isStudentOrAlumniRole || isAdminOrFHTRole || isScholarshipCoordinatorUser);

  return (
    <AwardPaymentLayout dataTestId='new-award-payment-request' bannerTitle={bannerTitle}>
      {(isAdminOrFHTRole || isScholarshipCoordinatorUser) && <TitleBar currentPage={title} paths={titleBarPaths} />}
      <AwardPaymentPageContainer>
        <AwardPaymentPageHeader title={headerTitle} />
        {showSpinnerWithMinTime ? (
          <div data-testid={isViewingExistingRequest ? 'award-payment-request-details-spinner' : 'new-award-payment-request-spinner'}>
            <PleaseWait isLoading={showSpinnerWithMinTime} />
          </div>
        ) : requestDetailsError ? (
          <ErrorAlert>{requestDetailsError}</ErrorAlert>
        ) : isViewingExistingRequest ? (
          requestDetails ? (
            <AwardPaymentRequestContent
              auth={authStore}
              initialFormState={mapAwardPaymentRequestDetailsToFormState(requestDetails)}
              readOnly={!isAwardFormEditable}
              setIsSubmitting={setIsSubmitting}
            />
          ) : (
            <ErrorAlert>Request details were not found.</ErrorAlert>
          )
        ) : (
          <AwardPaymentRequestContent
            auth={authStore}
            prefilledYourInformationState={prefilledYourInformationState}
            readOnly={false}
            setIsSubmitting={setIsSubmitting}
          />
        )}
      </AwardPaymentPageContainer>
    </AwardPaymentLayout>
  );
};

const AwardPaymentRequest = inject('AuthStore', 'SeniorStore')(observer(AwardPaymentRequestComponent));

export default AwardPaymentRequest;
